import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import JournalEntry from "./components/JournalEntry";
import ModificationLogDrawer from "./components/ModificationLogDrawer";
import Loader from "../../Loader";
import EmptyState from "../../EmptyState";

import {blueColor} from "assets/jss/main";

import {getModificationLog} from "api/product/modificationLog";

import useScrollTrigger from "hooks/useScrollTrigger";

ModificationLog.propTypes = {
    productId: PropTypes.string,
    refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)])
};

function ModificationLog({productId, refreshKey}) {
    const [logs, setLogs] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(1);

    const [sectionRef, triggered] = useScrollTrigger(productId);

    useEffect(() => {
        if (!triggered || !productId) {
            return;
        }

        setLoading(true);
        setPage(1);
        getModificationLog(productId)
            .then((res) => {
                setLogs(res.logs);
                setTotalItems(res.totalItems);
            })
            .finally(() => setLoading(false));
    }, [triggered, productId, refreshKey]);

    function loadMore() {
        const nextPage = page + 1;

        setLoadingMore(true);
        getModificationLog(productId, nextPage)
            .then((res) => {
                setLogs((currentLogs) => [...currentLogs, ...res.logs]);
                setTotalItems(res.totalItems);
            })
            .finally(() => setLoadingMore(false));

        setPage(nextPage);
    }

    const hasMore = logs.length < totalItems;

    if (!productId) {
        return null;
    }

    return (
        <div ref={sectionRef}>
            {loading && <Loader />}

            {!loading && logs.length === 0 && <EmptyState />}

            {!loading && logs.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '100%',
                }}>
                    {logs.slice(0, 3).map((log) => (
                        <JournalEntry
                            key={log.id}
                            compact
                            action={log.action}
                            field={log.field}
                            variationSku={log.sku}
                            oldValue={log.oldValue}
                            newValue={log.newValue}
                            date={log.date}
                            author={log.user.firstName}
                        />
                    ))}

                    <span
                        onClick={() => setOpen(true)}
                        style={{
                            color: blueColor,
                            fontSize: '14px',
                            cursor: 'pointer',
                            width: 'fit-content',
                            fontWeight: 400
                        }}>
                        <FormattedMessage id='product.show.modificationLog.seeAll' />
                    </span>
                    <ModificationLogDrawer
                        logs={logs}
                        open={open}
                        onClose={() => setOpen(false)}
                        onLoadMore={loadMore}
                        loadingMore={loadingMore}
                        hasMore={hasMore}
                    />
                </div>
            )}
        </div>
    );
}

export default ModificationLog;
